import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material'
import { SmokingRooms as EmptyIcon, SmokingRooms as SmokeIcon } from '@mui/icons-material'
import { useGetCigarsQuery, Cigar } from '@store/api/cigarApi'
import { useAppSelector } from '@store/hooks'

/**
 * Group cigars by brand + cigar line + vitola, summing their quantities.
 */
export function groupCigars(cigars: Cigar[]): Cigar[] {
  const groups = new Map<string, Cigar>()
  for (const cigar of cigars) {
    // Skip fully smoked cigars (quantity 0)
    if (cigar.quantity <= 0) continue
    const key = `${cigar.brandId}:${cigar.cigarLineId}:${cigar.vitolaId}`
    const existing = groups.get(key)
    if (existing) {
      existing.quantity += cigar.quantity
      // If any cigar in the group is available, the group is available
      if (cigar.status === 'available') {
        existing.status = 'available'
      }
    } else {
      groups.set(key, { ...cigar })
    }
  }
  return Array.from(groups.values())
}

type SortField = 'brand' | 'name' | 'quantity'
type SortOrder = 'asc' | 'desc'

interface HumidorContentsProps {
  humidorId: string
  onViewCigar?: (cigar: Cigar) => void
  onStartSession?: (cigar: Cigar) => void
}

const HumidorContents: React.FC<HumidorContentsProps> = ({ humidorId, onViewCigar, onStartSession }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [sortField, setSortField] = useState<SortField>('brand')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  const { data, isLoading, error } = useGetCigarsQuery(
    { humidorId, limit: 100, page: 1 },
    { skip: !isAuthenticated || !humidorId }
  )

  const sortedCigars = useMemo(() => {
    if (!data?.cigars) return []
    return groupCigars(data.cigars).sort((a, b) => {
      let aVal: string | number
      let bVal: string | number
      switch (sortField) {
        case 'brand':
          aVal = a.brand.name.toLowerCase()
          bVal = b.brand.name.toLowerCase()
          break
        case 'name':
          aVal = a.cigarLine.name.toLowerCase()
          bVal = b.cigarLine.name.toLowerCase()
          break
        case 'quantity':
          aVal = a.quantity
          bVal = b.quantity
          break
        default:
          aVal = a.brand.name.toLowerCase()
          bVal = b.brand.name.toLowerCase()
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [data?.cigars, sortField, sortOrder])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success'
      case 'smoked': return 'default'
      case 'traded': return 'info'
      case 'gifted': return 'warning'
      case 'damaged': return 'error'
      default: return 'default'
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        Failed to load cigars in this humidor.
      </Alert>
    )
  }

  if (!data?.cigars || data.cigars.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <EmptyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary" gutterBottom>
          This humidor is empty
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add cigars to this humidor to see them here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {sortedCigars.reduce((sum, c) => sum + c.quantity, 0)} cigar{sortedCigars.reduce((sum, c) => sum + c.quantity, 0) !== 1 ? 's' : ''} ({sortedCigars.length} type{sortedCigars.length !== 1 ? 's' : ''})
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'brand'}
                  direction={sortField === 'brand' ? sortOrder : 'asc'}
                  onClick={() => handleSort('brand')}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Line / Vitola
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'quantity'}
                  direction={sortField === 'quantity' ? sortOrder : 'asc'}
                  onClick={() => handleSort('quantity')}
                >
                  Qty
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              {onStartSession && <TableCell align="center">Smoke</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCigars.map((cigar) => (
              <TableRow
                key={cigar.id}
                hover
                onClick={() => onViewCigar?.(cigar)}
                sx={{ cursor: onViewCigar ? 'pointer' : 'default' }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {cigar.brand.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{cigar.cigarLine.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cigar.vitola.name}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{cigar.quantity}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={cigar.status}
                    color={getStatusColor(cigar.status) as any}
                    size="small"
                  />
                </TableCell>
                {onStartSession && (
                  <TableCell align="center">
                    {cigar.status === 'available' && (
                      <Tooltip title="Smoke this cigar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation()
                            onStartSession(cigar)
                          }}
                        >
                          <SmokeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default HumidorContents
